import React, { useCallback, useEffect, useId, useState } from 'react';
import { cn } from '../../../lib/utils';
import { Z_CLASS } from '../../../lib/z-index';
import { useMediaQuery } from '../../../hooks/useMediaQuery';
import { subscribeWalkthroughLayoutChange } from './walkthroughLayout';

const SPOTLIGHT_PADDING = 8;
const BACKDROP_ALPHA = 0.55;

/**
 * @param {DOMRect | null} rect
 * @param {number} padding
 */
function inflateRect(rect, padding) {
  return {
    top: rect.top - padding,
    left: rect.left - padding,
    width: rect.width + padding * 2,
    height: rect.height + padding * 2,
  };
}

/**
 * @param {Object} props
 * @param {HTMLElement | null} props.targetElement
 * @param {boolean} [props.visible]
 */
export function WalkthroughSpotlight({ targetElement, visible = true }) {
  const maskId = `walkthrough-spotlight-${useId().replace(/:/g, '')}`;
  const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  const [rect, setRect] = useState(null);

  const measure = useCallback(() => {
    if (!targetElement) {
      setRect(null);
      return;
    }
    setRect(targetElement.getBoundingClientRect());
  }, [targetElement]);

  useEffect(() => {
    measure();
  }, [measure]);

  useEffect(() => {
    if (!targetElement) return undefined;

    const handleUpdate = () => measure();

    window.addEventListener('resize', handleUpdate);
    window.addEventListener('orientationchange', handleUpdate);
    document.addEventListener('scroll', handleUpdate, true);

    const resizeObserver =
      typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver(handleUpdate)
        : null;
    resizeObserver?.observe(targetElement);

    const unsubscribeLayout = subscribeWalkthroughLayoutChange(handleUpdate);

    return () => {
      window.removeEventListener('resize', handleUpdate);
      window.removeEventListener('orientationchange', handleUpdate);
      document.removeEventListener('scroll', handleUpdate, true);
      resizeObserver?.disconnect();
      unsubscribeLayout();
    };
  }, [targetElement, measure]);

  if (!visible || !rect) return null;

  const spotlight = inflateRect(rect, SPOTLIGHT_PADDING);

  return (
    <div
      className={cn(Z_CLASS.WALKTHROUGH, 'pointer-events-none fixed inset-0')}
      aria-hidden="true"
    >
      <svg className="pointer-events-none fixed inset-0 h-full w-full">
        <defs>
          <mask id={maskId}>
            <rect x="0" y="0" width="100%" height="100%" fill="white" />
            <rect
              x={spotlight.left}
              y={spotlight.top}
              width={spotlight.width}
              height={spotlight.height}
              rx="8"
              fill="black"
            />
          </mask>
        </defs>
        <rect
          x="0"
          y="0"
          width="100%"
          height="100%"
          fill={`rgba(0, 0, 0, ${BACKDROP_ALPHA})`}
          mask={`url(#${maskId})`}
        />
        <rect
          x={spotlight.left}
          y={spotlight.top}
          width={spotlight.width}
          height={spotlight.height}
          rx="8"
          fill="none"
          stroke="rgb(59, 130, 246)"
          strokeWidth="2"
          className={cn(!prefersReducedMotion && 'animate-pulse')}
        />
      </svg>
    </div>
  );
}

export default WalkthroughSpotlight;
